import { useState, useEffect } from 'react';
import { subscribeToEmotes, subscribeToThemes, subscribeToSystemConfig, getEmojis } from '../services/firestore.js';

export function useEmoteTheme() {
    const [emotes, setEmotes] = useState([]);
    const [themes, setThemes] = useState([]);
    const [activeThemeId, setActiveThemeId] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let unsubscribeEmotes = null;
        let unsubscribeThemes = null;
        let unsubscribeConfig = null;

        const init = async () => {
            // 1. Load Legacy Emojis (Standard)
            let legacy = [];
            try {
                legacy = await getEmojis('superSubscriber'); // Fetch all
            } catch (e) {
                console.error("Failed to load legacy emojis in hook", e);
            }

            // 2. Subscribe to Custom Emotes & Themes
            unsubscribeEmotes = subscribeToEmotes((customData) => {
                // Merge legacy and custom, flagging custom
                const customWithFlag = customData.map(e => ({ ...e, isCustom: true }));
                setEmotes([...legacy, ...customWithFlag]);
            });

            unsubscribeThemes = subscribeToThemes(setThemes);
            unsubscribeConfig = subscribeToSystemConfig((config) => {
                setActiveThemeId(config?.activeThemeId || null);
            });

            setLoading(false);
        };

        init();

        return () => {
            if (unsubscribeEmotes) unsubscribeEmotes();
            if (unsubscribeThemes) unsubscribeThemes();
            if (unsubscribeConfig) unsubscribeConfig();
        };
    }, []);

    const activeTheme = activeThemeId ? themes.find(t => t.id === activeThemeId) : null;

    // Helper to find emote by any identifier (Id, Code, Emoji Char)
    const findEmote = (key) => {
        if (!key) return null;
        const cleanKey = typeof key === 'string' ? key.trim() : key;

        return emotes.find(e =>
            e.id === cleanKey ||
            e.code === cleanKey ||
            e.emoji === cleanKey ||
            (typeof cleanKey === 'string' && cleanKey.startsWith(':') && e.code === cleanKey.slice(1, -1))
        );
    };

    // Helper to get resolved URL
    const getEmoteUrl = (key) => {
        const emote = findEmote(key);
        if (!emote) return null;

        // Check for Theme Override
        if (activeTheme && activeTheme.overrides && activeTheme.overrides[emote.id]) {
            return activeTheme.overrides[emote.id];
        }

        return emote.url;
    };

    // Helper to get full object for reactions
    const getEmoteObject = (key) => {
        const emote = findEmote(key);
        if (!emote) return null;

        const url = getEmoteUrl(key);
        return { ...emote, displayUrl: url };
    };

    // --- ACCESS CONTROL LOGIC ---
    const checkEmoteAccess = (emoteId, user) => {
        const emote = findEmote(emoteId);
        if (!emote) return { hasAccess: false, reason: "Emote not found" };

        const userTier = user?.subscriptionTier || 'global';
        const badges = user?.badges || [];
        const unlockedBadges = badges.filter(b => b.unlocked).length;

        // 1. Tier Check
        let allowedTier = false;
        if (emote.tier === 'global') allowedTier = true;
        else if (emote.tier === 'subscriber') allowedTier = ['subscriber', 'super_subscriber', 'superSubscriber', 'mega_fan'].includes(userTier);
        else if (['super_fan', 'superSubscriber'].includes(emote.tier)) allowedTier = ['super_subscriber', 'superSubscriber', 'mega_fan'].includes(userTier);
        else if (emote.tier === 'mega_fan') allowedTier = userTier === 'mega_fan';

        if (!allowedTier) return { hasAccess: false, reason: "Subscription Tier required" };

        // 2. Gamification Check (Badge Unlocks)
        // Logic: 0 badges = 2, 1 = 4, 2 = 6, 3 = 8, 4+ = All
        if (unlockedBadges >= 4) return { hasAccess: true };

        const unlockLimit = 2 + (unlockedBadges * 2);

        // We need to find the index of this emote within its group (Tier + Type)
        // This MUST match EmotePicker sorting
        const isCustom = !!emote.isCustom;

        // Filter peers
        const peers = emotes.filter(e =>
            !!e.isCustom === isCustom &&
            e.tier === emote.tier
        );

        // Sort peers (Match EmotePicker/Firestore/Service logic)
        peers.sort((a, b) => {
            if (isCustom) {
                // Custom: Order (asc) -> CreatedAt (desc)
                const orderA = a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
                const orderB = b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
                if (orderA !== orderB) return orderA - orderB;
                return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
            } else {
                // Legacy: sortOrder
                return (a.sortOrder || 0) - (b.sortOrder || 0);
            }
        });

        const index = peers.findIndex(e => e.id === emote.id);

        if (index === -1) return { hasAccess: false, reason: "Error finding emote index" };

        if (index < unlockLimit) return { hasAccess: true };

        // Calculate needed badges
        const needed = Math.ceil((index + 1 - 2) / 2) - unlockedBadges;
        return { hasAccess: false, reason: `Locked: Need ${needed} more badge(s)`, badgeLock: true };
    };

    return {
        emotes,
        themes,
        activeThemeId,
        activeTheme,
        getEmoteUrl,
        getEmoteObject,
        checkEmoteAccess,
        loading
    };
}
